import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import StorageIcon from "@mui/icons-material/Storage";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GlobalStyles from "@mui/material/GlobalStyles";
import PenPaperIcon from "../../components/icons/PenPaperIcon";

import {
  fetchCostAnalysis,
  submitCostAnalysis,
  addLineItem,
  updateLineItem,
  deleteLineItem,
} from "./types/cost-analysis-api";
import type {
  CategoryType,
  CostAnalysisDetail,
  CostCategory,
  CostLineItem,
  SubmitTarget,
} from "./types/cost-analysis";
import { usePrintPortal } from "./types/use-print-portal";

// NOTE: `CostLineItem` needs a `detail?: string | null` field added in
// ./types/cost-analysis.ts (subtitle text like "4 crew members × $2.00"),
// matching the new `detail` column on the backend model/serializer.

const CATEGORY_ICON: Record<CategoryType, ReactNode> = {
  travel: <LocalShippingOutlinedIcon fontSize="small" />,
  crew: <GroupsIcon fontSize="small" />,
  catering: <RestaurantIcon fontSize="small" />,
  data: <StorageIcon fontSize="small" />,
  other: <MoreHorizIcon fontSize="small" />,
};

const SUBMIT_TARGETS: { value: SubmitTarget; label: string }[] = [
  { value: "finance", label: "Finance" },
];

function formatCurrency(value: string | number) {
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(n)) return "$0.00";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
}

function parseAmount(value: string) {
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
}

/** Inline label + amount editor, shared by "add item" and "edit item" flows. */
function LineItemForm({
  initialLabel = "",
  initialAmount = "",
  saving,
  confirmLabel,
  onCancel,
  onSave,
  labelLocked = false,
}: {
  initialLabel?: string;
  initialAmount?: string;
  saving: boolean;
  confirmLabel: string;
  onCancel: () => void;
  onSave: (label: string, amount: number) => void;
  labelLocked?: boolean;
}) {
  const [label, setLabel] = useState(initialLabel);
  const [amount, setAmount] = useState(initialAmount);

  const parsed = parseAmount(amount);
  const canSave = label.trim().length > 0 && parsed !== null;

  return (
    <Stack
      direction="row"
      spacing={1}
      onClick={(e) => e.stopPropagation()}
      sx={{ alignItems: "center" }}
    >
      <TextField
        size="small"
        placeholder="Label"
        value={label}
        autoFocus={!labelLocked}
        disabled={labelLocked}
        onChange={(e) => setLabel(e.target.value)}
        sx={{ flex: 1 }}
      />
      <TextField
        size="small"
        placeholder="Amount"
        type="number"
        value={amount}
        autoFocus={labelLocked}
        onChange={(e) => setAmount(e.target.value)}
        sx={{ width: 120 }}
      />
      <Tooltip title={confirmLabel}>
        <span>
          <IconButton
            size="small"
            color="primary"
            disabled={!canSave || saving}
            onClick={() => canSave && onSave(label.trim(), parsed as number)}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <IconButton size="small" onClick={onCancel} disabled={saving}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

/** A single line item row: view state, or swapped for an inline edit / delete-confirm state.
 *  Edit/delete actions reveal on hover — no separate "editing mode" needed.
 *  View state shows `label` as the heading and, when present, `detail` as
 *  a muted subtitle underneath (e.g. "4 crew members × $2.00") instead of
 *  the old dash-separated label + "auto" badge. */
function LineItemRow({
  item,
  onUpdate,
  onDelete,
}: {
  item: CostLineItem;
  onUpdate: (label: string, amount: number) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [mode, setMode] = useState<"view" | "edit" | "confirm-delete">("view");
  const [busy, setBusy] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleSave = async (label: string, amount: number) => {
    setBusy(true);
    try {
      // Locked items keep their existing label regardless of form state.
      await onUpdate(item.is_locked ? item.label : label, amount);
      setMode("view");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await onDelete();
    } finally {
      setBusy(false);
    }
  };

  if (mode === "edit") {
    return (
      <LineItemForm
        initialLabel={item.label}
        initialAmount={String(item.amount)}
        saving={busy}
        confirmLabel="Save changes"
        onCancel={() => setMode("view")}
        onSave={handleSave}
        labelLocked={item.is_locked}
      />
    );
  }

  if (mode === "confirm-delete") {
    return (
      <Stack
        direction="row"
        spacing={1.5}
        onClick={(e) => e.stopPropagation()}
        sx={{ alignItems: "center" }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary", flex: 1 }}>
          Delete {item.label}?
        </Typography>
        <Button
          size="small"
          color="error"
          variant="contained"
          disableElevation
          disabled={busy}
          onClick={handleDelete}
          sx={{ textTransform: "none" }}
        >
          {busy ? "Deleting…" : "Delete"}
        </Button>
        <Button
          size="small"
          disabled={busy}
          onClick={() => setMode("view")}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
      </Stack>
    );
  }

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 1,
        minHeight: 32,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ color: "text.primary", fontWeight: 500, fontSize: 13 }}
        >
          {item.label}
        </Typography>
        {item.detail && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: 12,
              display: "block",
              mt: 0.25,
            }}
          >
            {item.detail}
          </Typography>
        )}
      </Box>

      <Stack
        direction="row"
        spacing={0.5}
        sx={{ alignItems: "center", flexShrink: 0 }}
      >
        <Typography
          variant="body2"
          sx={{ color: "text.primary", fontWeight: 700 }}
        >
          {formatCurrency(item.amount)}
        </Typography>
        <Stack
          direction="row"
          onClick={(e) => e.stopPropagation()}
          sx={{
            opacity: hovered ? 1 : 0,
            transition: "opacity 100ms ease",
            pointerEvents: hovered ? "auto" : "none",
          }}
        >
          <IconButton size="small" onClick={() => setMode("edit")}>
            <PenPaperIcon sx={{ fontSize: 16 }} />
          </IconButton>
          {/* Locked (fixed-slot) items can't be deleted */}
          {!item.is_locked && (
            <IconButton size="small" onClick={() => setMode("confirm-delete")}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

function ApprovedStamp() {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 24,
        right: 24,
        border: "3px solid #4C7A3D",
        borderRadius: 2,
        px: 3,
        py: 1,
        transform: "rotate(-8deg)",
        opacity: 0.85,
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 900,
          letterSpacing: "0.15em",
          color: "#4C7A3D",
        }}
      >
        APPROVED
      </Typography>
    </Box>
  );
}
function SignatureLine({ label }: { label: string }) {
  return (
    <Box sx={{ flex: 1 }}>
      <Box sx={{ borderBottom: "1px solid #3B2E27", height: 48 }} />
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", mt: 0.5, display: "block" }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function CategoryRow({
  category,
  onAddLineItem,
  onUpdateLineItem,
  onDeleteLineItem,
}: {
  category: CostCategory;
  onAddLineItem: (
    categoryId: number,
    label: string,
    amount: number,
  ) => Promise<void>;
  onUpdateLineItem: (
    categoryId: number,
    itemId: number,
    label: string,
    amount: number,
  ) => Promise<void>;
  onDeleteLineItem: (categoryId: number, itemId: number) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [addSaving, setAddSaving] = useState(false);

  const hasItems = category.line_items.length > 0;

  // Flag when the line items don't add up to the category's stated total —
  // a quick way to catch stale or manually-overridden amounts.
  const computedTotal = category.line_items.reduce(
    (sum, i) => sum + Number(i.amount || 0),
    0,
  );
  const statedTotal = Number(category.amount || 0);
  const isMismatched = hasItems && Math.abs(computedTotal - statedTotal) > 0.01;

  const handleAdd = async (label: string, amount: number) => {
    setAddSaving(true);
    try {
      await onAddLineItem(category.id, label, amount);
      setAddingItem(false);
      setOpen(true);
    } finally {
      setAddSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        borderColor: open ? "#D9CFC7" : "divider",
        borderRadius: 3,
        overflow: "hidden",
        transition: "border-color 120ms ease",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2.5,
          py: 2,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: open ? "primary.main" : "#ECE7E2",
            color: open ? "primary.contrastText" : "#6E645D",
          }}
        >
          {CATEGORY_ICON[category.category_type]}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.3 }}
          >
            {category.category_type_display}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            {hasItems
              ? `${category.line_items.length} item${category.line_items.length === 1 ? "" : "s"}`
              : "No items yet"}
          </Typography>
        </Box>

        {isMismatched && (
          <Tooltip
            title={`Line items total ${formatCurrency(computedTotal)}, category total is ${formatCurrency(statedTotal)}`}
          >
            <WarningAmberIcon sx={{ fontSize: 18, color: "warning.main" }} />
          </Tooltip>
        )}

        <Typography
          sx={{ fontWeight: 700, color: "text.primary", whiteSpace: "nowrap" }}
        >
          {formatCurrency(category.amount)}
        </Typography>

        <IconButton
          size="small"
          disableRipple
          sx={{ color: "text.secondary", ml: 0.5, pointerEvents: "none" }}
          aria-hidden
        >
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider sx={{ borderColor: "divider" }} />
        <Box sx={{ px: 2.5, py: 2 }}>
          <Stack
            spacing={1.25}
            divider={<Divider flexItem sx={{ borderColor: "#EFEAE5" }} />}
          >
            {category.line_items.map((item) => (
              <LineItemRow
                key={item.id}
                item={item}
                onUpdate={(label, amount) =>
                  onUpdateLineItem(category.id, item.id, label, amount)
                }
                onDelete={() => onDeleteLineItem(category.id, item.id)}
              />
            ))}

            {!hasItems && !addingItem && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontStyle: "italic" }}
              >
                Nothing here yet.
              </Typography>
            )}

            {category.category_type === "other" && !addingItem && (
              <Button
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                onClick={(e) => {
                  e.stopPropagation();
                  setAddingItem(true);
                }}
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Add line item
              </Button>
            )}

            {category.category_type === "other" && addingItem && (
              <LineItemForm
                saving={addSaving}
                confirmLabel="Add item"
                onCancel={() => setAddingItem(false)}
                onSave={handleAdd}
              />
            )}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}

function InfoCard({
  media,
  eyebrow,
  title,
  detailIcon,
  detail,
}: {
  media: ReactNode;
  eyebrow: string;
  title: string;
  detailIcon: ReactNode;
  detail: string;
}) {
  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        flex: 1,
        borderColor: "divider",
        borderRadius: 3,
        p: 2,
        display: "flex",
        gap: 2,
        alignItems: "center",
      }}
    >
      {media}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 11,
            letterSpacing: "0.08em",
            fontWeight: 700,
            color: "text.secondary",
          }}
        >
          {eyebrow}
        </Typography>
        <Typography
          sx={{ fontWeight: 700, color: "text.primary", mt: 0.25 }}
          noWrap
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.5,
            color: "text.secondary",
          }}
        >
          {detailIcon}
          <Typography variant="body2" sx={{ color: "text.primary" }} noWrap>
            {detail}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default function CostAnalysis({ id: idProp }: { id?: string | number }) {
  const params = useParams<{ id?: string }>();
  const id = idProp ?? params?.id;

  const [data, setData] = useState<CostAnalysisDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const triggerPrint = usePrintPortal("cost-analysis-print-area");
  const [sendAnchor, setSendAnchor] = useState<HTMLElement | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [toast, setToast] = useState<{
    severity: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    fetchCostAnalysis(id)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? "Failed to load cost analysis");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const venue = data?.venue;

  const handleSend = async (target: SubmitTarget) => {
    if (!data) return;
    setSendAnchor(null);
    setSubmitting(true);
    try {
      const updated = await submitCostAnalysis(data.id, target);
      setData(updated);
      setToast({
        severity: "success",
        message: `Sent to ${SUBMIT_TARGETS.find((t) => t.value === target)?.label}`,
      });
    } catch (err) {
      setToast({
        severity: "error",
        message: (err as Error).message ?? "Failed to send",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddLineItem = async (
    categoryId: number,
    label: string,
    amount: number,
  ) => {
    if (!data) return;
    try {
      const updated = await addLineItem(data.id, {
        category_id: categoryId,
        label,
        amount,
      });
      setData(updated);
    } catch (err) {
      setToast({
        severity: "error",
        message: (err as Error).message ?? "Failed to add line item",
      });
      throw err;
    }
  };

  const handleUpdateLineItem = async (
    categoryId: number,
    itemId: number,
    label: string,
    amount: number,
  ) => {
    if (!data) return;
    try {
      const updated = await updateLineItem(data.id, itemId, { label, amount });
      setData(updated);
    } catch (err) {
      setToast({
        severity: "error",
        message: (err as Error).message ?? "Failed to update line item",
      });
      throw err;
    }
  };

  const handleDeleteLineItem = async (categoryId: number, itemId: number) => {
    if (!data) return;
    try {
      const updated = await deleteLineItem(data.id, itemId);
      setData(updated);
    } catch (err) {
      setToast({
        severity: "error",
        message: (err as Error).message ?? "Failed to delete line item",
      });
      throw err;
    }
  };

  const sendDisabled = useMemo(
    () =>
      submitting ||
      data?.status === "awaiting_audit" ||
      data?.status === "approved",
    [submitting, data?.status],
  );

  const computedGrandTotal =
    data?.categories.reduce((sum, c) => sum + Number(c.amount || 0), 0) ?? 0;
  const grandTotalMismatched =
    !!data &&
    Math.abs(computedGrandTotal - Number(data.total_amount || 0)) > 0.01;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ maxWidth: 640, mx: "auto", py: 10 }}>
        <Alert severity="error">{error ?? "Cost analysis not found"}</Alert>
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          "@media print": {
            "@page": { margin: "0.5in" },
            "#cost-analysis-print-area": {
              padding: "24px",
            },
          },
        }}
      />
      <Box
        id="cost-analysis-print-area"
        sx={{
          maxWidth: 1240,
          mx: "auto",
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 5 },
          position: "relative",
        }}
      >
        {data.status === "approved" && <ApprovedStamp />}
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                letterSpacing: "0.06em",
                fontWeight: 700,
                color: "text.secondary",
              }}
            >
              COST ANALYSIS
            </Typography>
            <Typography
              sx={{
                fontSize: 20,
                color: "text.primary",
                fontWeight: 700,
                mt: 0.5,
              }}
            >
              {data.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.25 }}
            >
              Case #{data.case_number}
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: "#E7D8CF",
              borderRadius: 2.5,
              px: 4,
              py: 2,
              textAlign: "center",
              minWidth: 220,
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ justifyContent: "center", alignItems: "center" }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  letterSpacing: "0.1em",
                  fontWeight: 700,
                  color: "#7C6A60",
                }}
              >
                TOTAL ESTIMATED COST
              </Typography>
              {grandTotalMismatched && (
                <Tooltip
                  title={`Categories add up to ${formatCurrency(computedGrandTotal)}`}
                >
                  <WarningAmberIcon
                    sx={{ fontSize: 14, color: "warning.dark" }}
                  />
                </Tooltip>
              )}
            </Stack>
            <Typography
              sx={{ fontSize: 26, fontWeight: 700, color: "#3B2E27", mt: 0.5 }}
            >
              {formatCurrency(data.total_amount)}
            </Typography>
          </Box>
        </Stack>

        {/* Info cards */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          sx={{ mt: 4 }}
        >
          <InfoCard
            media={
              venue?.cover_photo ? (
                <Box
                  component="img"
                  src={venue.cover_photo}
                  alt={venue?.name ?? "Venue"}
                  sx={{
                    width: 116,
                    height: 92,
                    borderRadius: 2,
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 116,
                    height: 92,
                    borderRadius: 2,
                    bgcolor: "#E5E5E5",
                    flexShrink: 0,
                  }}
                />
              )
            }
            eyebrow="PRIMARY VENUE"
            title={venue?.name ?? "No venue linked"}
            detailIcon={<PlaceOutlinedIcon sx={{ fontSize: 16 }} />}
            detail={venue?.address ?? "—"}
          />
          <InfoCard
            media={
              <Box
                sx={{
                  width: 92,
                  height: 92,
                  borderRadius: 2,
                  bgcolor: "#E5E5E5",
                  flexShrink: 0,
                }}
              />
            }
            eyebrow="CLIENT RECORD"
            title={data.title}
            detailIcon={<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />}
            detail={data.client_ref ? `Billing Ref: #${data.client_ref}` : ""}
          />
        </Stack>

        {/* Breakdown */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "baseline",
            mt: 5,
            mb: 2,
          }}
        >
          <Typography
            sx={{ fontSize: 18, color: "text.primary", fontWeight: 700 }}
          >
            Breakdown Categories
          </Typography>
        </Stack>

        <Stack spacing={2}>
          {data.categories.map((category) => (
            <CategoryRow
              key={category.id}
              category={category}
              onAddLineItem={handleAddLineItem}
              onUpdateLineItem={handleUpdateLineItem}
              onDeleteLineItem={handleDeleteLineItem}
            />
          ))}
        </Stack>

        {/* Signature section — for manual sign-off after printing */}
        <Stack
          direction="row"
          spacing={4}
          sx={{ mt: 5, pt: 3, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Stack spacing={2.5} sx={{ flex: 1 }}>
            <SignatureLine label="Finance Signature" />
          </Stack>
          <Stack spacing={2.5} sx={{ flex: 1 }}>
            <SignatureLine label="Date" />
          </Stack>
        </Stack>
        {/* Footer */}
        <Stack
          direction="row"
          sx={{
            mt: 5,
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between",
            alignItems: "center",
            "@media print": { display: "none" },
          }}
        >
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<PrintOutlinedIcon />}
              onClick={triggerPrint}
              sx={{
                textTransform: "none",
                color: "text.primary",
                borderColor: "#D9D2CC",
                bgcolor: "background.paper",
                px: 2.5,
                py: 1.1,
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#C3B9B0",
                  bgcolor: "background.paper",
                },
              }}
            >
              Print
            </Button>
          </Stack>

          <Box sx={{ display: "flex" }}>
            <Button
              variant="contained"
              disableElevation
              disabled={sendDisabled}
              onClick={(e) => setSendAnchor(e.currentTarget)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                px: 4,
                py: 1.25,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }}
            >
              {data.status === "awaiting_audit"
                ? `Sent to ${data.submitted_to}`
                : "Send"}
            </Button>
            <Button
              variant="contained"
              disableElevation
              disabled={sendDisabled}
              aria-label="Send options"
              onClick={(e) => setSendAnchor(e.currentTarget)}
              sx={{
                minWidth: 44,
                px: 0,
                borderLeft: "1px solid rgba(255,255,255,0.18)",
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
              }}
            >
              <ArrowDropDownIcon />
            </Button>
          </Box>

          <Menu
            anchorEl={sendAnchor}
            open={Boolean(sendAnchor)}
            onClose={() => setSendAnchor(null)}
          >
            {SUBMIT_TARGETS.map((t) => (
              <MenuItem key={t.value} onClick={() => handleSend(t.value)}>
                {t.label}
              </MenuItem>
            ))}
          </Menu>
        </Stack>

        <Snackbar
          open={Boolean(toast)}
          autoHideDuration={4000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          {toast ? (
            <Alert severity={toast.severity} onClose={() => setToast(null)}>
              {toast.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </Box>
    </>
  );
}
